import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { get, post } from "../../utils/http";

const { Title } = Typography;

interface UserRecord {
  id: number;
  username: string;
  name: string;
  phone: string;
  avatar: string;
  points: number;
  is_deleted: boolean;
  created_at: string;
}

interface EntitlementRecord {
  id: number;
  scope_type: string;
  module_id?: number | null;
  access_type: string;
  source: string;
  grant_reason?: string;
  start_at?: string | null;
  end_at?: string | null;
}

interface ModuleOption {
  id: number;
  module_name: string;
}

function UserList() {
  const [dataSource, setDataSource] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [modules, setModules] = useState<ModuleOption[]>([]);

  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [entitlementModalVisible, setEntitlementModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserRecord | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementRecord[]>([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [pointsForm] = Form.useForm();
  const [entitlementForm] = Form.useForm();

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const res = await get("/api/manage/user-list", { page, offset: 20 });
      if (res.data.code === 0) {
        setDataSource(res.data.data.list || []);
        setTotal(res.data.data.total || 0);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("获取用户列表失败:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await get("/api/manage/module-list");
      if (res.data.code === 0) {
        setModules(
          Array.isArray(res.data.data.list)
            ? res.data.data.list.map((item: any) => ({
                id: item.id,
                module_name: item.module_name,
              }))
            : [],
        );
      }
    } catch (error) {
      message.error("获取模块列表失败");
    }
  };

  useEffect(() => {
    fetchUsers(1);
    fetchModules();
  }, []);

  const handleAdjustPoints = (record: UserRecord) => {
    setCurrentUser(record);
    pointsForm.setFieldsValue({
      points_change: 10,
      description: "管理员手动调整",
    });
    setAdjustModalVisible(true);
  };

  const handleAdjustSubmit = async () => {
    try {
      const values = await pointsForm.validateFields();
      const res = await post("/api/manage/adjust-points", {
        user_id: currentUser?.id,
        points_change: values.points_change,
        description: values.description,
      });

      if (res.data.code === 0) {
        message.success("调整成功");
        setAdjustModalVisible(false);
        fetchUsers(currentPage);
      }
    } catch (err) {
      console.error("调整积分失败:", err);
    }
  };

  const openEntitlementModal = async (record: UserRecord) => {
    setCurrentUser(record);
    setEntitlementModalVisible(true);
    entitlementForm.setFieldsValue({
      scope_type: "module",
      access_type: "permanent",
      reason: "管理员手动发放",
    });
    setEntitlementsLoading(true);
    try {
      const res = await get("/api/manage/user-entitlements", { user_id: record.id });
      if (res.data.code === 0) {
        setEntitlements(res.data.data.list || []);
      }
    } catch (error) {
      message.error("获取用户权益失败");
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleGrantEntitlement = async () => {
    try {
      const values = await entitlementForm.validateFields();
      const payload = {
        user_id: currentUser?.id,
        scope_type: values.scope_type,
        module_id: values.scope_type === "module" ? values.module_id : undefined,
        access_type: "permanent",
        reason: values.reason,
      };

      const res = await post("/api/manage/grant-entitlement", payload);
      if (res.data.code === 0) {
        message.success("权益发放成功");
        openEntitlementModal(currentUser as UserRecord);
      }
    } catch (error) {
      console.error("发放权益失败:", error);
    }
  };

  const columns = [
    {
      title: "头像",
      dataIndex: "avatar",
      key: "avatar",
      width: 80,
      render: (avatar: string) => <Avatar src={avatar || "/static/logo.png"} />,
    },
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "姓名",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "手机号",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "积分余额",
      dataIndex: "points",
      key: "points",
      render: (points: number) => (
        <Tag color="gold" style={{ fontWeight: "bold" }}>
          {points || 0}
        </Tag>
      ),
    },
    {
      title: "注册时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
    },
    {
      title: "状态",
      dataIndex: "is_deleted",
      key: "is_deleted",
      render: (isDeleted: boolean) => (
        <Tag color={isDeleted ? "error" : "success"}>
          {isDeleted ? "已注销" : "正常"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 220,
      render: (_: any, record: UserRecord) => (
        <Space>
          <Button type="link" onClick={() => handleAdjustPoints(record)}>
            调整积分
          </Button>
          <Button type="link" onClick={() => openEntitlementModal(record)}>
            权益管理
          </Button>
        </Space>
      ),
    },
  ];

  const entitlementColumns = [
    {
      title: "范围",
      dataIndex: "scope_type",
      key: "scope_type",
      render: (value: string, record: EntitlementRecord) =>
        value === "all"
          ? "全站永久"
          : `模块 #${record.module_id ?? "-"}`,
    },
    {
      title: "类型",
      dataIndex: "access_type",
      key: "access_type",
      render: (value: string) =>
        value === "permanent" ? <Tag color="gold">永久</Tag> : <Tag color="blue">临时</Tag>,
    },
    {
      title: "来源",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "开始时间",
      dataIndex: "start_at",
      key: "start_at",
      render: (value: string) => value || "-",
    },
    {
      title: "结束时间",
      dataIndex: "end_at",
      key: "end_at",
      render: (value: string | null) => value || "永久有效",
    },
    {
      title: "备注",
      dataIndex: "grant_reason",
      key: "grant_reason",
      render: (value: string) => value || "-",
    },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            用户管理
          </Title>
        }
      >
        <Table
          loading={loading}
          dataSource={dataSource}
          columns={columns}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: 20,
            total,
            showTotal: (value) => `共 ${value} 条`,
            onChange: (page) => fetchUsers(page),
          }}
        />
      </Card>

      <Modal
        title={`调整用户积分: ${currentUser?.name || currentUser?.username || ""}`}
        open={adjustModalVisible}
        onOk={handleAdjustSubmit}
        onCancel={() => setAdjustModalVisible(false)}
        okText="确认调整"
      >
        <Form form={pointsForm} layout="vertical">
          <Form.Item
            label="调整数值 (正数为加分，负数为扣分)"
            name="points_change"
            rules={[{ required: true, message: "请输入调整数值" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="调整原因"
            name="description"
            rules={[{ required: true, message: "请输入调整原因" }]}
          >
            <Input placeholder="例如: 运营活动赠送" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`权益管理: ${currentUser?.name || currentUser?.username || ""}`}
        open={entitlementModalVisible}
        onCancel={() => setEntitlementModalVisible(false)}
        onOk={handleGrantEntitlement}
        okText="发放权益"
        width={1000}
      >
        <Space direction="vertical" size={16} style={{ display: "flex" }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="用户">{currentUser?.username}</Descriptions.Item>
            <Descriptions.Item label="姓名">{currentUser?.name}</Descriptions.Item>
            <Descriptions.Item label="手机号">{currentUser?.phone}</Descriptions.Item>
            <Descriptions.Item label="当前积分">{currentUser?.points}</Descriptions.Item>
          </Descriptions>

          <Card size="small" title="发放新权益">
            <Form form={entitlementForm} layout="vertical">
              <Space align="start" style={{ width: "100%" }}>
                <Form.Item
                  label="授权范围"
                  name="scope_type"
                  rules={[{ required: true, message: "请选择授权范围" }]}
                  style={{ minWidth: 160 }}
                >
                  <Select
                    options={[
                      { label: "单模块", value: "module" },
                      { label: "全站永久", value: "all" },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.scope_type !== cur.scope_type}
                >
                  {({ getFieldValue }) =>
                    getFieldValue("scope_type") === "module" ? (
                      <Form.Item
                        label="模块"
                        name="module_id"
                        rules={[{ required: true, message: "请选择模块" }]}
                        style={{ minWidth: 220 }}
                      >
                        <Select
                          options={modules.map((module) => ({
                            label: module.module_name,
                            value: module.id,
                          }))}
                        />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>

                <Tag color="gold">永久权益</Tag>
              </Space>

              <Form.Item
                label="备注"
                name="reason"
                rules={[{ required: true, message: "请输入备注" }]}
              >
                <Input placeholder="例如：客服补偿、活动赠送、人工补单" />
              </Form.Item>
            </Form>
          </Card>

          <Card size="small" title="当前有效权益">
            <Table
              loading={entitlementsLoading}
              dataSource={entitlements}
              columns={entitlementColumns}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: "暂无有效权益" }}
            />
          </Card>
        </Space>
      </Modal>
    </div>
  );
}

export default UserList;
